// Defesa em profundidade contra perda de identidade do paciente.
//
// Hoje a identidade (patientId, fullName, patientEmail, patientCpf,
// patientPhone) e' guardada em localStorage. Auditoria 13-15/05 mostrou
// pacientes em chat pago perdendo o LS por motivos diversos (Safari ITP,
// Brave storage cleanup, refresh manual em mobile baixo, cache buster do
// index.html, modo navegacao privada, etc) e voltando a Home — onde clicam
// "Iniciar consulta" e geram um SEGUNDO chat com mesmo nome.
//
// Este modulo adiciona 2 camadas de backup ALEM do LS, que sao
// transparentes ao app (write-through silencioso):
//
//   1. Cookie first-party (Set-Cookie via document.cookie):
//      Sobrevive melhor a Safari ITP que LS — Apple limpa LS apos 7 dias
//      sem interacao first-party, mas cookies first-party so sao limpos
//      apos 7 dias sem cookie/storage interaction (regra mais permissiva).
//      TTL: 90 dias.
//
//   2. IndexedDB (consultamais_session db, ja existente — usado pelo
//      cache-buster como fallback). Adicionamos write-through (todo SET
//      em LS espelha pra IDB) para que o cache-buster tenha snapshot
//      sempre atualizado.
//
// O modulo NAO muda fluxo: continua escrevendo em LS+SS (camadas
// principais). Os backups sao silenciosos. A leitura tenta LS->SS->Cookie
// ->IDB nessa ordem.
//
// ESCOPO LIMITADO: apenas as 5 chaves de identidade. Outras chaves
// (paymentConfirmed, finalizedAt, etc.) continuam apenas em LS.

const IDENTITY_KEYS = ['patientId', 'fullName', 'patientName', 'patientEmail', 'patientCpf', 'patientPhone'];
const COOKIE_PREFIX = 'cm_id_';
const COOKIE_TTL_DAYS = 90;
const IDB_NAME = 'consultamais_session';
const IDB_STORE = 'session';
const IDB_KEY = 'session_snapshot';

// ─── Cookies (first-party, secure, lax) ──────────────────────────────
const isHttps = () => {
  try { return typeof window !== 'undefined' && window.location.protocol === 'https:'; } catch { return false; }
};

const setCookie = (name, value, days = COOKIE_TTL_DAYS) => {
  try {
    if (typeof document === 'undefined') return;
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    const secure = isHttps() ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires}; SameSite=Lax${secure}`;
  } catch {}
};

const getCookie = (name) => {
  try {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch { return null; }
};

const removeCookie = (name) => {
  try {
    if (typeof document === 'undefined') return;
    const secure = isHttps() ? '; Secure' : '';
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secure}`;
  } catch {}
};

// ─── IndexedDB write-through ─────────────────────────────────────────
let idbCache = null;
const getIdb = () => new Promise((resolve) => {
  if (idbCache) return resolve(idbCache);
  try {
    if (typeof indexedDB === 'undefined') return resolve(null);
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      try {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
      } catch {}
    };
    req.onsuccess = () => { idbCache = req.result; resolve(idbCache); };
    req.onerror = () => resolve(null);
  } catch { resolve(null); }
});

const idbSnapshotWriteThrough = (() => {
  // Coalesce multiple writes into 1 idle commit per ~200ms para nao
  // sobrecarregar IDB em rajadas (ex.: form com varios setItem em sequencia).
  let pending = null;
  let scheduled = false;
  const flush = async () => {
    scheduled = false;
    const snap = pending;
    pending = null;
    if (!snap) return;
    const db = await getIdb();
    if (!db) return;
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      // Merge com o snapshot existente (preservar chaves que esse modulo nao gerencia,
      // ex.: chaves antigas do cache buster de outras sessoes).
      const getReq = store.get(IDB_KEY);
      getReq.onsuccess = () => {
        try {
          const prev = (getReq.result && typeof getReq.result === 'object') ? getReq.result : {};
          const merged = { ...prev, ...snap, _updatedAt: Date.now() };
          store.put(merged, IDB_KEY);
        } catch {}
      };
    } catch {}
  };
  return (key, value) => {
    pending = pending || {};
    if (value == null) delete pending[key];
    else pending[key] = String(value);
    if (!scheduled) {
      scheduled = true;
      setTimeout(flush, 200);
    }
  };
})();

const idbReadAll = async () => {
  const db = await getIdb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(IDB_KEY);
      req.onsuccess = () => resolve(req.result && typeof req.result === 'object' ? req.result : null);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
};

// ─── API publica ─────────────────────────────────────────────────────

/**
 * Escreve uma chave de identidade nas 4 camadas: LS, SS, cookie, IDB.
 * Se a chave nao for de identidade, escreve apenas em LS+SS (compat).
 *
 * Eh chamado pelos pontos do app que ja escreviam em LS — drop-in.
 * Nao bloqueia: cookie e IDB sao best-effort silencioso.
 */
export const persistIdentityKey = (key, value) => {
  try { localStorage.setItem(key, value); } catch {}
  try { sessionStorage.setItem(key, value); } catch {}
  if (IDENTITY_KEYS.includes(key)) {
    try { setCookie(`${COOKIE_PREFIX}${key}`, value); } catch {}
    try { idbSnapshotWriteThrough(key, value); } catch {}
  }
};

/**
 * Remove uma chave em todas as camadas.
 */
export const clearIdentityKey = (key) => {
  try { localStorage.removeItem(key); } catch {}
  try { sessionStorage.removeItem(key); } catch {}
  if (IDENTITY_KEYS.includes(key)) {
    try { removeCookie(`${COOKIE_PREFIX}${key}`); } catch {}
    try { idbSnapshotWriteThrough(key, null); } catch {}
  }
};

/**
 * Le uma chave priorizando: LS -> SS -> cookie -> IDB.
 * Retorna null se nao achar em lugar nenhum.
 *
 * IDB e' assincrono — se esta funcao for chamada em codigo sincrono,
 * retorna sem consultar IDB. Use `getIdentityKeyAsync` quando puder.
 */
export const getIdentityKey = (key) => {
  try { const v = localStorage.getItem(key); if (v != null) return v; } catch {}
  try { const v = sessionStorage.getItem(key); if (v != null) return v; } catch {}
  if (IDENTITY_KEYS.includes(key)) {
    try { const v = getCookie(`${COOKIE_PREFIX}${key}`); if (v != null) return v; } catch {}
  }
  return null;
};

/**
 * Versao async que tambem consulta IDB. Use no boot do app.
 */
export const getIdentityKeyAsync = async (key) => {
  const sync = getIdentityKey(key);
  if (sync != null) return sync;
  if (!IDENTITY_KEYS.includes(key)) return null;
  try {
    const snap = await idbReadAll();
    if (snap && snap[key] != null) return String(snap[key]);
  } catch {}
  return null;
};

/**
 * Recovery completa no boot: para cada IDENTITY_KEY ausente em LS,
 * tenta restaurar de cookie ou IDB. Eh idempotente (so escreve se faltar).
 *
 * Chame UMA VEZ no init do app, antes de o React montar componentes que
 * dependem de identidade.
 *
 * Retorna { restored: { key: source } } com as chaves que foram restauradas
 * e a fonte (cookie/idb) — util para telemetria.
 */
export const restoreIdentityFromBackups = async () => {
  const restored = {};
  let idbSnap = null;
  for (const key of IDENTITY_KEYS) {
    let lsVal = null;
    try { lsVal = localStorage.getItem(key); } catch {}
    if (lsVal != null) continue;

    // Tenta cookie primeiro (sincrono e geralmente mais confiavel que IDB
    // que pode estar bloqueado em modo privado).
    let v = null; let source = null;
    try { v = getCookie(`${COOKIE_PREFIX}${key}`); if (v != null) source = 'cookie'; } catch {}

    if (v == null) {
      if (idbSnap === null) idbSnap = await idbReadAll();
      if (idbSnap && idbSnap[key] != null) { v = String(idbSnap[key]); source = 'idb'; }
    }

    if (v != null) {
      try { localStorage.setItem(key, v); } catch {}
      try { sessionStorage.setItem(key, v); } catch {}
      restored[key] = source;
    }
  }
  return { restored };
};

/**
 * Espelha para cookie + IndexedDB toda chave de identidade que já exista no
 * localStorage, independente de quem a escreveu.
 *
 * `persistIdentityKey` só protege os pontos que o chamam: em 29/09/2026 havia
 * 58 gravações diretas de `localStorage.setItem('patientId'|...)` no app
 * contra 11 arquivos usando a camada de backup. O efeito medido é que o
 * navegador perde identidade muito mais que o app nativo (patientId presente
 * em 59,1% dos boots contra 87,3%) e ainda assim restaura do backup MENOS
 * (709 contra 1.014 sessões) — não havia o que restaurar, porque o valor
 * nunca chegou ao cookie nem ao IDB. O sintoma na ponta é
 * `payment_screen_orphan_id_generated`: 158 vezes no navegador, 9 no app.
 *
 * Reconciliar no boot cobre todos os pontos de escrita de uma vez, sem
 * precisar refatorar os 58. Idempotente e silencioso.
 *
 * @returns {{mirrored: string[]}} chaves que existiam em LS e foram espelhadas
 */
export const syncIdentityBackups = () => {
  const mirrored = [];
  for (const key of IDENTITY_KEYS) {
    let v = null;
    try { v = localStorage.getItem(key); } catch {}
    if (v == null) {
      try { v = sessionStorage.getItem(key); } catch {}
    }
    if (v == null || v === '') continue;
    try { setCookie(`${COOKIE_PREFIX}${key}`, v); } catch {}
    try { idbSnapshotWriteThrough(key, v); } catch {}
    mirrored.push(key);
  }
  return { mirrored };
};

export const IDENTITY_KEYS_LIST = IDENTITY_KEYS.slice();
